import { useAppLayoutId } from '@src/hooks/useAppLayoutId';
import { useUser } from '@src/hooks/useUser';
import { getNotificationRenderInfo } from '@src/providers/AppNotifications/getNotificationRenderInfo';
import { useLazyGetNotificationByIdQuery } from '@src/providers/AppNotifications/redux/AppNotificationsApi';
import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import { navigationSliceActions } from '@src/providers/AppStateProvider/navigationSlice';
import { NotificationVariants, logger, useGlobalToasts } from '@talos/kyoko';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const NotificationDeepLinkRedirect = () => {
  const { notificationId } = useParams<{ notificationId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppStateDispatch();
  const selectedLayoutId = useAppLayoutId('trading');
  const user = useUser();
  const { add: addToast } = useGlobalToasts();
  const hasTriggeredRef = useRef(false);
  const [triggerGetNotification] = useLazyGetNotificationByIdQuery();

  useEffect(() => {
    if (hasTriggeredRef.current || !notificationId) {
      return;
    }

    hasTriggeredRef.current = true;
    navigate('/', { replace: true });

    triggerGetNotification({ notificationId })
      .unwrap()
      .then(notification => {
        let targetUrl: string | undefined;
        if (notification) {
          const notificationRenderInfo = getNotificationRenderInfo(notification, false);
          targetUrl = notificationRenderInfo.targetUrlResolver?.(selectedLayoutId) ?? notificationRenderInfo.targetUrl;
        }
        if (targetUrl) {
          dispatch(navigationSliceActions.navigate(targetUrl));
        } else {
          logger.warn('[NotificationDeepLinkRedirect] No target URL for notification', {
            extra: { notificationId, user: user.Name, type: notification?.Type, subType: notification?.SubType },
          });
          addToast({ text: 'Failed to load notification.', variant: NotificationVariants.Negative });
        }
      })
      .catch((error: unknown) => {
        logger.warn('[NotificationDeepLinkRedirect] Failed to fetch notification', {
          extra: { notificationId, user: user.Name, error },
        });
        addToast({ text: 'Failed to load notification.', variant: NotificationVariants.Negative });
      });
  }, [notificationId, navigate, dispatch, user.Name, triggerGetNotification, addToast, selectedLayoutId]);

  return null;
};
